/**
 * 🔐 Unified Auth0 Provider for Coach Cedar
 * Cross-platform authentication for web (Auth0 React) and native (react-native-auth0)
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { Platform } from 'react-native';
import { useRouter } from 'expo-router';
import { navigateAfterLogout } from './navigateAfterLogout';

// Web-specific imports (conditional)
let Auth0ProviderWeb: any = null;
let useAuth0Web: any = null;

if (Platform.OS === 'web') {
  try {
    const auth0React = require('@auth0/auth0-react');
    Auth0ProviderWeb = auth0React.Auth0Provider;
    useAuth0Web = auth0React.useAuth0;
  } catch (e) {
    console.warn('Auth0 React SDK not available');
  }
}

// Native-specific imports (conditional)
let Auth0Native: any = null;

if (Platform.OS !== 'web') {
  try {
    Auth0Native = require('react-native-auth0').default;
  } catch (e) {
    console.warn('react-native-auth0 not available');
  }
}

import { auth0Config } from './auth0-config';
import { getTokenManager, type RefreshFailure } from './tokenManager';
import { attemptSessionRecovery } from './sessionRecovery';
import { secureTokenStorage } from '../services/secureTokenStorage';
import { readBootCredentials } from './bootCredentials';
import { storage } from '../services/storage';
import { clearPushPermissionLedger } from '../services/pushPermissionLedger';
import { API_BASE_URL } from '../utils/api';
import { readAndClearPendingAttribution } from '../utils/attributionCapture';
import {
  mergePartnerIntoAttribution,
  readPartnerRecordForSignup,
  waitForInstallCheckIn,
} from '../services/partnerAttribution';
import { syncFeatureFlags } from '../services/featureFlagsSync';
import { fetchAuthBounded, verifyAuthBounded } from './authBoundedFetch';
import {
  watchHeldSession,
  HELD_SESSION_BACKOFF_MS,
  type HeldSessionCheck,
  type HeldSessionTrigger,
  type HeldSessionWatch,
} from './heldSessionWatch';
import { reportClientError } from '../services/clientErrorReporter';
import { markForcedLogout } from '../services/authForcedLogoutMarker';
import {
  syncHealthConsent,
  syncPhoneHealthPipesEstablished,
} from '../services/healthConsentSync';
import { syncFirstPlanCreatedAt, clearFirstPlanCreatedAt } from '../services/firstPlanSync';

// The S2-1 module-level refresh mutex lived here until 2026-08-14. It is gone
// on purpose: it protected only callers INSIDE this React tree, it returned
// `false` (= "refresh failed") to whichever caller lost the race — which the
// boot path then treated as a dead credential — and it could not coordinate
// with the services that refresh through fetchWithAuth. TokenManager's
// single-flight promise replaces it and covers every caller, returning the
// WINNER's result to everyone instead of a fake failure.

interface Auth0User {
  sub: string;
  email?: string;
  email_verified?: boolean;
  name?: string;
  picture?: string;
  [key: string]: any;
}

interface Auth0ContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: Auth0User | null;
  error: Error | null;
  loginWithRedirect: (options?: any) => Promise<void>;
  loginWithPopup: (options?: any) => Promise<void>;
  logout: (options?: any) => void;
  getAccessTokenSilently: (options?: any) => Promise<string>;
  // Native iOS Apple Sign-In via Auth0 token-exchange (bypasses Universal Login → no consent screen).
  // Pass the Apple authorizationCode + (optional, first-login-only) fullName from
  // expo-apple-authentication's signInAsync(). Native-only — web/Android no-op.
  loginWithNativeApple: (params: {
    authorizationCode: string;
    fullName?: { firstName: string; lastName: string };
  }) => Promise<void>;
  // Native Google Sign-In via Auth0 token-exchange (bypasses Universal Login → no consent screen).
  // Pass the Google ID token from @react-native-google-signin/google-signin.signIn().
  // Native-only (iOS + Android) — web no-op.
  loginWithNativeGoogle: (params: {
    idToken: string;
  }) => Promise<void>;
}

const Auth0Context = createContext<Auth0ContextValue | null>(null);

interface UnifiedAuth0ProviderProps {
  children: React.ReactNode;
}

/**
 * Unified Auth0 Provider - automatically uses correct SDK based on platform
 */

/**
 * Mirror everything /api/auth/me carries into the local caches that gate features.
 *
 * ⚠️ CALL THIS FROM EVERY AUTH-SUCCESS PATH. Until 2026-08-13 this block was inlined in
 * exactly ONE of five: the stored-token cold-boot verification. The other four —
 * token refresh, loginWithRedirect, loginWithNativeApple, loginWithNativeGoogle —
 * set `isAuthenticated` and never hydrated. `_currentFlags` is module-scope, starts
 * `{}`, and every gate reads `=== true`, so those users ran an entire session with
 * every flag-gated capability silently OFF: session reminders, BLE sensors, the
 * in-timer sensor sheet, cancel-and-edit, voice completion, both Essentials composer
 * surfaces, plus the native watch-scheduling kill switch never being pushed.
 *
 * That is why Matt's workout-reminder UI vanished and did NOT come back when he
 * re-signed in — only a full app restart (which takes the stored-token path) restored
 * it. It also means EVERY new user's first session — signup, onboarding, the session
 * that matters most — has run with every gated feature off.
 *
 * Each helper swallows its own errors, so this never throws and never blocks a login.
 */
async function hydrateSessionFromAuthMe(userData: any): Promise<void> {
  if (!userData) return;
  try {
    // ⚠️ Flags are gated on the SAME bit as the user state below.
    //
    // They used to sync unconditionally, on the reasoning that skipping them
    // would re-create the vanished-reminder bug on every DB hiccup. That is
    // backwards for the case that actually matters. `_currentFlags` is a
    // REPLACE, not a merge (featureFlagsSync), so on a degraded 200 — which
    // carries feature_flags={} — an unconditional sync OVERWRITES the user's
    // real cached flags with defaults and flips every `=== true` gate off
    // mid-session. Skipping keeps the last known-good flags, which is the
    // outcome the original reasoning wanted. On a cold session the cache is
    // empty either way, so nothing is lost.
    //
    // It also stops a degraded response pushing setKillSwitch(false) into iOS
    // UserDefaults, which would lift a backend-set watch-scheduling kill for a
    // user the backend had deliberately killed.
    if (userData.user_state_loaded !== false) {
      await syncFeatureFlags({
        ...(userData.feature_flags ?? {}),
        // voice_completion_enabled is a COMPUTED top-level /auth/me field
        // (FF_VOICE_COMPLETION + tier — routers/auth.py), folded into the flags cache
        // so useVoiceCompletionEnabled reads it like every other capability gate.
        voice_completion_enabled: userData.voice_completion_enabled === true,
        // D5 (2026-09-22): the triathlon coaching tier, the same way. Only the two
        // values the server sends are carried; anything else (absent on an old
        // server, the gate off) stays absent, which every reader treats as "not
        // gated" — so the upgrade teaser cannot appear by accident.
        ...(userData.tri_coaching_tier === 'gated' || userData.tri_coaching_tier === 'included'
          ? { tri_coaching_tier: userData.tri_coaching_tier }
          : {}),
      });
    }
    // ⚠️ ONLY persist these when the server actually loaded them.
    //
    // /api/auth/me wraps its user-state SELECT in a catch-all that returns HTTP 200
    // with SAFE DEFAULTS on a transient DB failure (routers/auth.py) — deliberately, so
    // a pool hiccup can't lock everyone out. But those defaults are
    // health_data_consent_at=null / pipes=[] / first_plan=null, and syncHealthConsent(null)
    // does AsyncStorage.removeItem. So a DB blip would ERASE the device's health-consent
    // record, and per the shared-iPhone gate (2026-05-29) that stops health sync until
    // the user re-grants — silently, which is the exact dark-pipe class this sprint exists
    // to kill. Hydrating on five paths instead of one multiplied the exposure.
    //
    // `user_state_loaded` is absent on older servers, which we treat as true: identical to
    // pre-2026-08-13 behaviour, so this is strictly a tightening.
    if (userData.user_state_loaded !== false) {
      await syncHealthConsent(userData.health_data_consent_at ?? null);
      await syncPhoneHealthPipesEstablished(userData.phone_health_pipes_established);
      await syncFirstPlanCreatedAt(userData.first_plan_created_at ?? null);
    } else if (__DEV__) {
      console.warn('⚠️ Auth: /auth/me returned placeholder user state — not persisting');
    }
  } catch (err) {
    if (__DEV__) console.warn('⚠️ Auth: session hydration failed (non-fatal):', err);
  }
}

/**
 * Fetch /api/auth/me with a freshly-minted token and hydrate from it.
 *
 * The login and refresh paths get their user object from /api/auth0/verify, whose
 * response shape does NOT carry feature_flags. So they cannot hydrate from what they
 * already have — they need this one extra GET. Failure is non-fatal: the user is
 * authenticated either way, and the next cold start will hydrate.
 */
async function hydrateSessionFromToken(cedarJwt?: string | null): Promise<void> {
  if (!cedarJwt) return;
  try {
    const res = await fetchAuthBounded(
      `${API_BASE_URL}/api/auth/me`,
      { headers: { Authorization: `Bearer ${cedarJwt}` } },
      'hydrate_from_token',
    );
    if (!res.ok) return;
    await hydrateSessionFromAuthMe(await res.json());
  } catch (err) {
    if (__DEV__) console.warn('⚠️ Auth: post-login hydration fetch failed (non-fatal):', err);
  }
}

/**
 * The `attribution` block every /api/auth0/verify sign-in sends (redirect,
 * native Apple, native Google). The backend reads it only when the call
 * CREATES the user; returning users ignore it.
 *
 * 1. The pending record from attributionCapture (web UTM / `?pc=`), read once
 *    and cleared, minus its local `captured_at` stamp.
 * 2. On native, the partner record from the first-launch check-in
 *    (services/partnerAttribution.ts) as `attribution.partner` — the shape
 *    `bind_signup` reads. Until 2026-09-23 this merge lived only in
 *    CallbackScreen.native.tsx, whose verify body ({code, state,
 *    redirect_uri}) the endpoint rejects, so no app signup ever carried its
 *    partner link. The sign-in waits (bounded, 2 s, never throws) for a
 *    check-in still on the wire so a fast signup does not race it. The merge
 *    never overwrites a partner click id already present. It ALWAYS carries
 *    this install's id (readPartnerRecordForSignup): the check-in writes its
 *    record before posting, and with no record at all the install id alone
 *    travels — so a signup that beats a slow check-in is remembered by the
 *    server and bound when the check-in's match arrives (migration 1821).
 *
 * Every step fails silent: attribution must never block a sign-in.
 */
async function readSignupAttribution(): Promise<Record<string, unknown> | undefined> {
  let attribution: Record<string, unknown> | undefined;
  try {
    const pending = await readAndClearPendingAttribution();
    if (pending) {
      const { captured_at: _ignored, ...forward } = pending;
      attribution = forward;
    }
  } catch (attrErr) {
    if (__DEV__) console.warn('UnifiedAuth0Provider: attribution read failed', attrErr);
  }
  if (Platform.OS !== 'web') {
    try {
      await waitForInstallCheckIn();
      attribution = mergePartnerIntoAttribution(attribution, await readPartnerRecordForSignup());
    } catch (partnerErr) {
      if (__DEV__) console.warn('UnifiedAuth0Provider: partner record read failed', partnerErr);
    }
  }
  return attribution;
}

export const UnifiedAuth0Provider: React.FC<UnifiedAuth0ProviderProps> = ({ children }) => {
  // Web platform: Use Auth0 React SDK directly
  if (Platform.OS === 'web' && Auth0ProviderWeb) {
    return (
      <Auth0ProviderWeb
        domain={auth0Config.domain}
        clientId={auth0Config.clientId}
        authorizationParams={{
          redirect_uri: auth0Config.redirectUri,
          audience: auth0Config.audience,
          scope: auth0Config.scope,
        }}
        useRefreshTokens={auth0Config.useRefreshTokens}
        cacheLocation={auth0Config.cacheLocation}
        httpTimeoutInSeconds={auth0Config.httpTimeoutInSeconds}
      >
        {children}
      </Auth0ProviderWeb>
    );
  }

  // Native platform: Use react-native-auth0 with custom provider
  return <NativeAuth0Provider>{children}</NativeAuth0Provider>;
};

/**
 * What a verify of the stored session concluded (NativeAuth0Provider,
 * `verifyStoredSession`). `inconclusive` carries why, for the device marker and
 * CloudWatch: `network`, `timeout`, `http_5xx`, `parse`, `unexpected_4xx`, or
 * `refresh_<failure>` for a 401 whose refresh could not reach an answer.
 */
type StoredSessionVerdict =
  | { kind: 'signed_in' }
  | { kind: 'refused' }
  | { kind: 'ended_elsewhere' }
  | { kind: 'inconclusive'; reason: string };

/**
 * Native Auth0 Provider - wraps react-native-auth0 in a React Context
 */
const NativeAuth0Provider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const router = useRouter();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<Auth0User | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [auth0Client, setAuth0Client] = useState<any>(null);
  // A stored session the boot could not verify is KEPT and checked again; this
  // is that check's schedule (heldSessionWatch.ts), null when nothing is kept.
  const heldWatchRef = useRef<HeldSessionWatch | null>(null);
  // Finished clears of the stored session. A verify that began before one
  // must not sign the athlete back in after it.
  const clearsRef = useRef(0);

  // The stored session was ended — by this provider, the entry route,
  // AuthContext or Settings: every sign-out goes through clearAll. Declared
  // before the boot effect so it is listening before the boot verify starts.
  useEffect(() => secureTokenStorage.onCleared(() => {
    clearsRef.current += 1;
    heldWatchRef.current?.stop();
    heldWatchRef.current = null;
    setUser(null);
    setIsAuthenticated(false);
  }), []);

  useEffect(() => {
    let disposed = false;

    async function initializeAuth() {
      let client: any = null;
      
      try {
        // Step 1: Initialize Auth0 client for native FIRST
        if (Auth0Native) {
          client = new Auth0Native({
            domain: auth0Config.domain,
            clientId: auth0Config.clientId,
          });
          setAuth0Client(client);

          // ── THE WIRE (2026-08-14) ─────────────────────────────────────────
          // TokenManager is the refresh machine every SERVICE reaches (via
          // fetchWithAuth). It shipped 2026-04-25 with this line missing, so
          // `this.auth0Client` was permanently null and EVERY refresh returned
          // null — the app had no mid-session token renewal at all. A 7-day JWT
          // was minted at login and then aged out with nothing renewing it; the
          // warm session simply started 401ing (p.rush12 810c13d3 measured in
          // production at age_s=605465 ≈ 7.0077d, and Matt's 2026-08-13 storm).
          //
          // The defect survived 3.5 months because of a name collision: the
          // useState setter one line above is ALSO called setAuth0Client, so
          // grepping the symbol finds a "caller" and the audit moves on. The
          // two are unrelated — one sets React state, this one wires the SDK
          // into the token manager. Keep both names in mind before "cleaning
          // up" either. Pinned by tests/gate/test_token_refresh_wired.py.
          getTokenManager().setAuth0Client(client);

          if (__DEV__) console.log('✅ Auth: Auth0 client initialized + token manager wired');
        }

        // Step 2: Check for stored tokens — read once the keychain can answer, so
        // a launch with the phone locked waits instead of skipping the verify
        // (bootCredentials.ts).
        const creds = await readBootCredentials();
        if (creds.cedarJwt.state === 'unreadable') {
          // On screen and still refused — a keychain fault, already reported.
          // Unauthenticated this launch; nothing cleared.
          console.error('❌ AUTH0_PROVIDER_KEYCHAIN_UNREADABLE — boot verify skipped; credentials kept');
          return;
        }
        const token = creds.cedarJwt.state === 'present' ? creds.cedarJwt.value : null;

        if (token) {
          if (__DEV__) console.log('✅ Auth: Found stored token, verifying...');
          const clearsAtStart = clearsRef.current;
          const verdict = await verifyStoredSession(token, clearsAtStart);
          if (verdict.kind === 'inconclusive') await holdSession(verdict.reason, clearsAtStart);
        } else {
          if (__DEV__) console.log('ℹ️ Auth: No stored token found');
        }
      } catch (error) {
        // What reaches here: the SDK failing to construct, or a refused
        // session whose clear failed. Either way, unauthenticated this launch.
        console.error(`❌ AUTH0_PROVIDER_INIT_FAILED error=${(error as Error)?.name ?? 'unknown'} — unauthenticated this launch`);
      } finally {
        setIsLoading(false);
      }
    }

    // Ask the server whether the stored JWT is good, and act on what it can
    // conclude.
    //
    // INCONCLUSIVE is never TERMINAL. No network, no answer within the boot
    // budget, a 5xx, a body that cannot be read, and a 401 whose refresh could
    // not reach Auth0 say nothing about the credential; they come back as
    // `inconclusive` for the caller to keep. Until 2026-10-14 this provider
    // kept the tokens on those answers but left the athlete UNAUTHENTICATED
    // for the launch, and every reader of it took that for signed out:
    // ChatScreen sent them to the login screen with a session nobody had
    // refused, and nothing checked again. The entry route (app/index.tsx,
    // `transient_failure` → into the app) and AuthContext (an unexpired JWT is
    // restored without asking) already treated the launch as signed in.
    //
    // The server's answer is read by `verifyAuth` — the entry route's resolver
    // — so both readers that ask it read it by one rule.
    async function verifyStoredSession(token: string, clearsAtStart: number): Promise<StoredSessionVerdict> {
      const result = await verifyAuthBounded(API_BASE_URL, token, 'boot_verify');

      if (result.kind === 'verified') {
        // Another reader ended the session while we asked: the server's yes is
        // about a credential this phone no longer holds.
        if (clearsRef.current !== clearsAtStart) return { kind: 'ended_elsewhere' };
        const userData = result.data;
        if (__DEV__) console.log('✅ Auth: Token valid, user authenticated');

        // /api/auth/me returns a flat object: {user_id, email, is_admin,
        // feature_flags}. Some other auth endpoints (e.g. /api/auth0/verify)
        // return a nested {user: {sub, email, ...}} shape. Construct an
        // Auth0User-shaped object when the flat form is received so the
        // context's `user` field is populated after stored-token session
        // restore (previously `setUser(userData.user)` resolved to
        // `undefined` here because /api/auth/me never emitted a `user`
        // field — `user` stayed null until the next refresh or login).
        // Prefer the nested shape when present for forward compatibility.
        const nextUser: Auth0User | null = userData.user
          ?? (userData.user_id
            ? {
                sub: userData.user_id,
                email: userData.email ?? undefined,
              }
            : null);
        setUser(nextUser);
        setIsAuthenticated(true);

        // Single hydration path — see hydrateSessionFromAuthMe for why this must
        // run on EVERY auth-success path, not just this one.
        await hydrateSessionFromAuthMe(userData);
        return { kind: 'signed_in' };
      }

      if (result.kind === 'unauthorized') {
        if (__DEV__) console.log('⚠️ Auth: Token invalid (401/403), trying refresh...');
        // Try to refresh. Only a refresh that Auth0 POSITIVELY refused may
        // end the session — a network failure here means "offline", and
        // clearing on it deletes the very refresh token that would have
        // recovered the user the moment they reconnected.
        const refreshed = await tryRefreshSession();
        if (refreshed.ok) return { kind: 'signed_in' };
        if (!refreshed.terminal) return { kind: 'inconclusive', reason: `refresh_${refreshed.failure}` };

        if (__DEV__) console.log('⚠️ Auth: Refresh rejected by Auth0, clearing all tokens');
        setUser(null);
        setIsAuthenticated(false);
        await secureTokenStorage.clearAll();
        // Refresh failed = this session is terminal (equivalent to a logout).
        // Clear the per-user push-permission ledger + first-plan anchor too
        // (both are GLOBAL keys, mirrored from the logout path below) so the
        // next user on a shared device can't inherit them (P2-6).
        await clearPushPermissionLedger();
        await clearFirstPlanCreatedAt();
        return { kind: 'refused' };
      }

      return { kind: 'inconclusive', reason: result.reason };
    }

    // Keep a session the server could not answer for: signed in, from what is
    // stored, and checked again (heldSessionWatch.ts) until a check concludes.
    async function holdSession(reason: string, clearsAtStart: number): Promise<void> {
      const stored = await secureTokenStorage.getUserData();
      // Another reader ended the session while the verify was out, or the
      // provider is gone: nothing to keep.
      if (disposed || clearsRef.current !== clearsAtStart) return;
      if (stored) setUser(stored as Auth0User);
      setIsAuthenticated(true);
      console.error(
        `❌ AUTH_BOOT_VERIFY_INCONCLUSIVE reason=${reason} — session kept: signed in, not verified; ` +
          `checking again on foreground, on reconnect and after ${HELD_SESSION_BACKOFF_MS.map((ms) => `${ms / 1000}s`).join('/')}`,
      );
      // The phone may be offline: the check that settles it reports the first
      // reason again (AuthBootVerifyRecovered / AuthBootVerifyRefused).
      void reportClientError({ surface: `AuthBootVerifyInconclusive.${reason}` });
      heldWatchRef.current = watchHeldSession((trigger) => checkHeldSession(reason, trigger));
    }

    // One check of a kept session. `settled` ends the watch.
    async function checkHeldSession(firstReason: string, trigger: HeldSessionTrigger): Promise<HeldSessionCheck> {
      try {
        const stored = await secureTokenStorage.readCedarJwt();
        if (stored.state === 'unreadable') return 'unsettled';
        if (stored.state === 'absent') {
          // Gone without a clear this provider heard: there is no session.
          console.error(`❌ AUTH_HELD_SESSION_GONE first=${firstReason} trigger=${trigger} — signed out`);
          setUser(null);
          setIsAuthenticated(false);
          return 'settled';
        }
        const verdict = await verifyStoredSession(stored.value, clearsRef.current);
        switch (verdict.kind) {
          case 'inconclusive':
            return 'unsettled';
          case 'signed_in':
            console.log(`AUTH_BOOT_VERIFY_RECOVERED first=${firstReason} trigger=${trigger}`);
            void reportClientError({ surface: `AuthBootVerifyRecovered.${firstReason}.${trigger}` });
            return 'settled';
          case 'refused':
            markForcedLogout('held_session_refused'); // E2
            void reportClientError({ surface: `AuthBootVerifyRefused.${firstReason}` });
            return 'settled';
          case 'ended_elsewhere':
            return 'settled';
        }
      } catch (e) {
        // A failed clear of a refused session, or a defect. The session stays
        // as it is and the next trigger checks again.
        console.error(`❌ AUTH_HELD_SESSION_CHECK_FAILED trigger=${trigger} error=${(e as Error)?.name ?? 'unknown'}`);
        return 'unsettled';
      }
    }

    // Refresh the session through the ONE refresh machine.
    //
    // Until 2026-08-14 this was a SECOND, independent implementation of the
    // same Auth0 -> userInfo -> /api/auth0/verify -> save sequence TokenManager
    // already owned, guarded by its own module-level boolean that returned
    // FALSE ("failed") to whichever caller lost the race — so a merely
    // CONCURRENT refresh was indistinguishable from a refused credential. Two
    // machines, two mutexes, no shared single-flight. Now: one machine, one
    // in-flight promise, and a failure that says WHY.
    //
    // `terminal` is the whole point. Only a credential Auth0 positively
    // refused — or the absence of one — may end a session. Offline, backend
    // hiccup, SDK-not-yet-wired all KEEP the tokens, because clearing deletes
    // the Auth0 refresh token that would have recovered the user silently.
    async function tryRefreshSession(): Promise<{ ok: boolean; terminal: boolean; failure: RefreshFailure }> {
      const recovery = await attemptSessionRecovery();

      if (recovery.kind === 'recovered') {
        const restored = await secureTokenStorage.getUserData();
        if (restored) setUser(restored as Auth0User);
        setIsAuthenticated(true);
        // /auth0/verify does not return feature_flags, so hydrate from the new
        // token. Without this a refreshed session runs with every gated
        // capability off (the vanished-reminder defect, #3072).
        await hydrateSessionFromToken(recovery.token);
        if (__DEV__) console.log('✅ Auth: Refresh complete, session restored');
        return { ok: true, terminal: false, failure: 'none' };
      }

      if (__DEV__) console.log(`⚠️ Auth: refresh failed (${recovery.failure}, terminal=${recovery.kind === 'terminal'})`);
      return { ok: false, terminal: recovery.kind === 'terminal', failure: recovery.failure };
    }

    initializeAuth();

    return () => {
      disposed = true;
      heldWatchRef.current?.stop();
      heldWatchRef.current = null;
    };
  }, []);

  // Re-sync React state when a refresh happens somewhere else.
  //
  // Now that the wire above exists, a refresh can be driven by any SERVICE
  // (fetchWithAuth -> TokenManager) mid-session, not only by this provider.
  // Without this subscription the storage layer would hold the new session
  // while the React tree kept rendering the old one — the same class of
  // split-state the sprint is closing, just one layer up. TokenManager's
  // docstring has specified this subscription since April; it was never wired.
  useEffect(() => {
    const unsubscribe = getTokenManager().subscribe(() => {
      void (async () => {
        try {
          // A logout racing an in-flight refresh must not resurrect the
          // session: if the tokens are gone, so is the reason to re-auth.
          const [jwt, restored] = await Promise.all([
            secureTokenStorage.getCedarJwt(),
            secureTokenStorage.getUserData(),
          ]);
          if (!jwt) return;
          if (restored) setUser(restored as Auth0User);
          setIsAuthenticated(true);
        } catch {
          // Best-effort state re-sync; the token itself is already persisted.
        }
      })();
    });
    return unsubscribe;
  }, []);

  const loginWithRedirect = async (options?: any) => {
    if (__DEV__) console.log('🔵 LOGIN WITH REDIRECT: START');
    
    if (!auth0Client) {
      if (__DEV__) console.log('❌ LOGIN: Auth0 client not initialized');
      throw new Error('Auth0 client not initialized');
    }

    try {
      setIsLoading(true);
      setError(null);

      if (__DEV__) console.log('🔵 Auth0: Starting authorization');

      // Step 1: Get credentials from Auth0
      // react-native-auth0 expects connection, maxAge, additionalParameters at TOP LEVEL (not inside authorizationParams).
      // Flatten so Auth0 receives prompt=login/select_account and max_age=0 for account picker / fresh auth.
      const ap = options?.authorizationParams;
      const authParams: Record<string, unknown> = {
        scope: auth0Config.scope,
        audience: auth0Config.audience,
        // redirectUrl intentionally omitted — react-native-auth0 auto-generates the correct
        // callback URL from customScheme + domain + Platform.OS + bundleIdentifier (agent.ts:54-55).
        // Passing our own is redundant and creates a maintenance surface where values could diverge.
        ...options,
      };
      if (ap) {
        if (ap.connection) authParams.connection = ap.connection;
        if (ap.max_age !== undefined) authParams.maxAge = ap.max_age;
        authParams.additionalParameters = {
          ...(ap.prompt && { prompt: ap.prompt }),
          ...(ap.max_age !== undefined && { max_age: String(ap.max_age) }),
          ...(ap.screen_hint && { screen_hint: ap.screen_hint }),
          ...(ap.additionalParameters || {}),
        };
      }
      delete authParams.authorizationParams;

      if (__DEV__) console.log('🔵 STEP 1: Calling auth0Client.webAuth.authorize...');
      let credentials;
      try {
        credentials = await auth0Client.webAuth.authorize(authParams);
      } catch (authErr: any) {
        // 🔧 FIX: "active transaction" means a previous auth flow (e.g. user dismissed
        // Safari/WebView) left Auth0's internal state dirty. Clear it and retry once.
        const msg = String(authErr?.message ?? '').toLowerCase();
        if (msg.includes('active transaction')) {
          if (__DEV__) console.log('⚠️ STEP 1: Active transaction detected — clearing session and retrying…');
          try { await auth0Client.webAuth.clearSession({ federated: false }); } catch (_) { /* ignore clear errors */ }
          credentials = await auth0Client.webAuth.authorize(authParams);
        } else {
          throw authErr;
        }
      }

      if (__DEV__) console.log('✅ STEP 1 COMPLETE: Got credentials');

      // Step 2: Get user info from Auth0
      if (__DEV__) console.log('🔵 STEP 2: Getting user info from Auth0...');
      const userInfo = await auth0Client.auth.userInfo({
        token: credentials.accessToken,
      });

      if (__DEV__) console.log('✅ STEP 2 COMPLETE: Got user info');

      // Apple Sign In fix: Apple only sends given_name/family_name in the initial
      // authorization response. Auth0's userInfo endpoint often returns empty names
      // for Apple users. The idToken JWT contains the claims from the original auth
      // response, so decode it to extract names as a fallback.
      let idTokenGivenName = '';
      let idTokenFamilyName = '';
      try {
        if (credentials.idToken) {
          // ADVERSARIAL FIX: atob() doesn't exist in React Native (Hermes).
          // Use Buffer.from (from 'buffer' npm package) on native, atob on web.
          const _b64 = credentials.idToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
          const _decoded = Platform.OS === 'web' ? atob(_b64) : require('buffer').Buffer.from(_b64, 'base64').toString('utf-8');
          const payload = JSON.parse(_decoded);
          idTokenGivenName = payload.given_name || payload.givenName || '';
          idTokenFamilyName = payload.family_name || payload.familyName || '';
          if (__DEV__) console.log('🔵 STEP 2.5: idToken claims — given_name:', idTokenGivenName, 'family_name:', idTokenFamilyName);
        }
      } catch (e) {
        if (__DEV__) console.log('⚠️ STEP 2.5: Could not decode idToken for name claims');
      }

      const mergedUserInfo = {
        ...userInfo,
        given_name: userInfo.givenName || userInfo.given_name || idTokenGivenName || '',
        family_name: userInfo.familyName || userInfo.family_name || idTokenFamilyName || '',
      };
      if (__DEV__) console.log('🔵 STEP 2.5: Final merged — given_name:', mergedUserInfo.given_name, 'family_name:', mergedUserInfo.family_name);

      // Step 3: Verify with Coach Cedar backend
      const backendUrl = API_BASE_URL;
      if (__DEV__) console.log('🔵 STEP 3: Verifying with backend...');

      // Platform Analytics — attach any pending attribution (web UTM captured at
      // landing; on native, the first-launch partner record — see
      // readSignupAttribution). Only meaningful on NEW user creation; the
      // backend ignores `attribution` for returning users.
      const attribution = await readSignupAttribution();

      const response = await fetch(`${backendUrl}/api/auth0/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          access_token: credentials.accessToken,
          auth0_user: mergedUserInfo,
          ...(attribution ? { attribution } : {}),
        }),
      });

      if (!response.ok) {
        if (__DEV__) console.log('❌ STEP 3 FAILED: Backend verification failed, status:', response.status);
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Backend verification failed');
      }

      const data = await response.json();
      if (__DEV__) console.log('✅ STEP 3 COMPLETE: Backend verification successful');

      // Step 4: Store tokens securely
      
      await secureTokenStorage.saveTokens(
        credentials.accessToken,
        credentials.refreshToken,
        data.token
      );
      
      await secureTokenStorage.saveUserData(data.user);

      if (__DEV__) console.log('✅ STEP 4 COMPLETE: Tokens and user data stored');

      // Step 4.5: Determine routing intent for LoginScreen

      // Always store the user's email so NameCollectionScreen can display it
      if (userInfo.email) {
        await storage.setItem('user_email', userInfo.email);
      }

      let routingIntent = 'app'; // default: go straight to main app

      // HIGHEST PRIORITY: email verification wall.
      // Email/password signups (post-cutoff) who haven't yet clicked
      // the verification link in their inbox must verify before touching
      // name-collection, onboarding, or chat. Apple/Google users are
      // pre-verified by the provider so this flag is always false for
      // them. See EMAIL_VERIFICATION_ENFORCEMENT_CUTOFF in
      // routers/auth0_auth.py for the grandfathering rule.
      if (data?.needsEmailVerification) {
        routingIntent = 'email-verification';
        if (__DEV__) console.log('🚧 STEP 4.5: User needs email verification — routing to wall');
      }

      // Next priority: does this user still need to provide their name?
      // Pass auth_method so backend can detect Apple Sign In users with names.
      // Skip the network call entirely if we already routed to the email
      // verification wall — name collection comes AFTER verification.
      const userAuthMethod = data.user?.authMethod || (mergedUserInfo?.sub?.startsWith('apple') ? 'apple' : '');
      if (__DEV__) console.log('🔵 STEP 4.5: auth_method for name check:', userAuthMethod);
      if (routingIntent === 'app') {
        try {
          const nameCheckResponse = await fetch(`${backendUrl}/api/users/needs-name-collection?auth_method=${encodeURIComponent(userAuthMethod)}`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${data.token}`,
            },
          });
          if (nameCheckResponse.ok) {
            const nameCheckData = await nameCheckResponse.json();
            if (__DEV__) console.log('🔵 STEP 4.5: name check response:', JSON.stringify(nameCheckData));
            if (nameCheckData.needs_name_collection) {
              // If Apple provided names, store them so NameCollectionScreen can pre-fill/hide fields
              if (nameCheckData.apple_provided_names && nameCheckData.first_name && nameCheckData.last_name) {
                await storage.setItem('siwa_first_name', nameCheckData.first_name);
                await storage.setItem('siwa_last_name', nameCheckData.last_name);
                // Timestamp lets NameCollectionScreen reject stale keys (>5 min old)
                // and prevents cross-user contamination if a different Apple ID logs in
                await storage.setItem('siwa_auth_timestamp', Date.now().toString());
                if (__DEV__) console.log('✅ STEP 4.5: Stored SIWA names for name collection screen');
              }
              routingIntent = 'name-collection';
              if (__DEV__) console.log('✅ STEP 4.5: User needs name collection');
            }
          } else {
            if (__DEV__) console.warn('⚠️ STEP 4.5: needs-name-collection check returned', nameCheckResponse.status);
          }
        } catch (nameCheckErr) {
          if (__DEV__) console.warn('⚠️ STEP 4.5: Could not check name-collection status (will fall through to onboarding check):', nameCheckErr);
        }
      }

      // If name is already present, check for general onboarding
      if (routingIntent === 'app' && data.needsOnboarding) {
        routingIntent = 'onboarding';
        if (__DEV__) console.log('✅ STEP 4.5: User needs onboarding');
      }

      await storage.setItem('login_routing_intent', routingIntent);
      if (__DEV__) console.log('✅ STEP 4.5 COMPLETE: Routing intent:', routingIntent);

      // Step 5: Update context state
      setUser(data.user || userInfo);
      setIsAuthenticated(true);

      // Hydrate feature flags / consent / pipes for THIS session. /auth0/verify's
      // response carries no feature_flags, so a fresh login previously left every
      // gated capability off until the next cold start.
      await hydrateSessionFromToken(data.token);

      // Step 5.5: Bridge user into AuthContext so useRequireAuth() sees the user
      // immediately. Without this, AuthContext.user stays null after login because
      // UnifiedAuth0Provider wraps AuthProvider and can't call useAuth().login().
      const bridgeUser = data.user || userInfo;
      try {
        const { notifyLogin } = require('../auth/authContextBridge');
        await notifyLogin({ id: bridgeUser.id, name: bridgeUser.name, email: bridgeUser.email });
        if (__DEV__) console.log('✅ STEP 5.5: AuthContext bridged');
      } catch (bridgeErr) {
        // Non-fatal — useRequireAuth fallback will restore from SecureStore
        if (__DEV__) console.warn('⚠️ STEP 5.5: AuthContext bridge failed (fallback will handle):', bridgeErr);
      }

      if (__DEV__) console.log('✅ AUTH COMPLETE: Ready to navigate');

      // Step 6: Navigation happens via LoginScreen watching isAuthenticated + login_routing_intent
      
    } catch (err) {
      if (__DEV__) console.error('❌ Auth0: Authorization failed:', err);
      setError(err as Error);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Native iOS Apple Sign-In via Auth0 token-exchange grant.
   * Bypasses Universal Login entirely → NO consent screen.
   *
   * Caller (LoginScreen.native.tsx) is responsible for getting the
   * authorizationCode + fullName from expo-apple-authentication.signInAsync().
   *
   * Requires Auth0 client to have:
   *  - app_type=native
   *  - native_social_login.apple.enabled=true
   *  - mobile.ios.app_bundle_identifier=com.coachcedar.app
   *  - Apple connection configured with Services ID + Team ID + Key ID + Private Key
   *
   * Note: this duplicates the post-credential flow from loginWithRedirect
   * (steps 2-6) intentionally. After validation we can extract a shared helper.
   */
  const loginWithNativeApple = async ({ authorizationCode, fullName }: {
    authorizationCode: string;
    fullName?: { firstName: string; lastName: string };
  }) => {
    if (__DEV__) console.log('🔵 LOGIN WITH NATIVE APPLE: START');

    if (!auth0Client) {
      throw new Error('Auth0 client not initialized');
    }

    try {
      setIsLoading(true);
      setError(null);

      // Step 1: Exchange Apple authorization code with Auth0 via token-exchange grant.
      // No webview, no Universal Login, no consent screen.
      if (__DEV__) console.log('🔵 STEP 1: Token exchange with Auth0...');
      const tokenExchangeBody: Record<string, unknown> = {
        grant_type: 'urn:ietf:params:oauth:grant-type:token-exchange',
        subject_token_type: 'http://auth0.com/oauth/token-type/apple-authz-code',
        subject_token: authorizationCode,
        client_id: auth0Config.clientId,
        audience: auth0Config.audience,
        scope: auth0Config.scope,
      };
      // Apple only sends fullName on the FIRST login. Pass it through so Auth0 creates
      // the user with the right name. Subsequent logins omit it (Apple doesn't re-send).
      if (fullName && (fullName.firstName || fullName.lastName)) {
        tokenExchangeBody.user_profile = JSON.stringify({
          name: { firstName: fullName.firstName, lastName: fullName.lastName },
        });
      }

      const tokenResponse = await fetch(`https://${auth0Config.domain}/oauth/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(tokenExchangeBody),
      });

      if (!tokenResponse.ok) {
        const errBody = await tokenResponse.json().catch(() => ({} as any));
        const msg = errBody.error_description || errBody.error || `HTTP ${tokenResponse.status}`;
        if (__DEV__) console.log('❌ STEP 1 FAILED: Token exchange returned', tokenResponse.status, errBody);
        throw new Error(`Apple token exchange failed: ${msg}`);
      }

      const tokens = await tokenResponse.json();
      const credentials = {
        accessToken: tokens.access_token,
        idToken: tokens.id_token,
        refreshToken: tokens.refresh_token,
      };
      if (__DEV__) console.log('✅ STEP 1 COMPLETE: Got Auth0 credentials via token exchange');

      // Step 2: Get user info from Auth0
      if (__DEV__) console.log('🔵 STEP 2: Getting user info from Auth0...');
      const userInfo = await auth0Client.auth.userInfo({
        token: credentials.accessToken,
      });
      if (__DEV__) console.log('✅ STEP 2 COMPLETE: Got user info');

      // Step 2.5: Decode idToken for Apple name claims (fallback if userInfo is empty)
      let idTokenGivenName = '';
      let idTokenFamilyName = '';
      try {
        if (credentials.idToken) {
          const _b64 = credentials.idToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
          const _decoded = Platform.OS === 'web' ? atob(_b64) : require('buffer').Buffer.from(_b64, 'base64').toString('utf-8');
          const payload = JSON.parse(_decoded);
          idTokenGivenName = payload.given_name || payload.givenName || '';
          idTokenFamilyName = payload.family_name || payload.familyName || '';
        }
      } catch (e) {
        if (__DEV__) console.log('⚠️ STEP 2.5: Could not decode idToken for name claims');
      }

      // Name priority: expo-apple-authentication fullName (most reliable, first-login only)
      // → idToken claims → userInfo. Apple's web-flow userInfo often returns empty names.
      const mergedUserInfo = {
        ...userInfo,
        given_name: (fullName?.firstName) || userInfo.givenName || userInfo.given_name || idTokenGivenName || '',
        family_name: (fullName?.lastName) || userInfo.familyName || userInfo.family_name || idTokenFamilyName || '',
      };
      if (__DEV__) console.log('🔵 STEP 2.5: Final merged — given_name:', mergedUserInfo.given_name, 'family_name:', mergedUserInfo.family_name);

      // Step 3: Verify with Coach Cedar backend
      const backendUrl = API_BASE_URL;
      if (__DEV__) console.log('🔵 STEP 3: Verifying with backend...');

      const attribution = await readSignupAttribution();

      const response = await fetch(`${backendUrl}/api/auth0/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_token: credentials.accessToken,
          auth0_user: mergedUserInfo,
          ...(attribution ? { attribution } : {}),
        }),
      });

      if (!response.ok) {
        if (__DEV__) console.log('❌ STEP 3 FAILED: Backend verification failed, status:', response.status);
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Backend verification failed');
      }

      const data = await response.json();
      if (__DEV__) console.log('✅ STEP 3 COMPLETE: Backend verification successful');

      // Step 4: Store tokens
      await secureTokenStorage.saveTokens(
        credentials.accessToken,
        credentials.refreshToken,
        data.token
      );
      await secureTokenStorage.saveUserData(data.user);
      // Hydrate feature flags / consent / pipes for THIS session. /auth0/verify's
      // response carries no feature_flags, so a fresh login previously left every
      // gated capability off until the next cold start.
      await hydrateSessionFromToken(data.token);
      if (__DEV__) console.log('✅ STEP 4 COMPLETE: Tokens and user data stored');

      // Step 4.5: Routing intent (mirrors loginWithRedirect Step 4.5)
      if (userInfo.email) {
        await storage.setItem('user_email', userInfo.email);
      }

      let routingIntent = 'app';
      // HIGHEST PRIORITY: email verification wall (mirrors loginWithRedirect logic).
      // Apple users return email_verified=true from the provider so this is a
      // defense-in-depth check rather than an expected hit on the native Apple path.
      if (data?.needsEmailVerification) {
        routingIntent = 'email-verification';
        if (__DEV__) console.log('🚧 STEP 4.5: User needs email verification — routing to wall');
      }
      const userAuthMethod = data.user?.authMethod || 'apple'; // native Apple flow
      if (__DEV__) console.log('🔵 STEP 4.5: auth_method for name check:', userAuthMethod);
      if (routingIntent === 'app') {
        try {
          const nameCheckResponse = await fetch(`${backendUrl}/api/users/needs-name-collection?auth_method=${encodeURIComponent(userAuthMethod)}`, {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${data.token}` },
          });
          if (nameCheckResponse.ok) {
            const nameCheckData = await nameCheckResponse.json();
            if (__DEV__) console.log('🔵 STEP 4.5: name check response:', JSON.stringify(nameCheckData));
            if (nameCheckData.needs_name_collection) {
              if (nameCheckData.apple_provided_names && nameCheckData.first_name && nameCheckData.last_name) {
                await storage.setItem('siwa_first_name', nameCheckData.first_name);
                await storage.setItem('siwa_last_name', nameCheckData.last_name);
                await storage.setItem('siwa_auth_timestamp', Date.now().toString());
                if (__DEV__) console.log('✅ STEP 4.5: Stored SIWA names for name collection screen');
              }
              routingIntent = 'name-collection';
              if (__DEV__) console.log('✅ STEP 4.5: User needs name collection');
            }
          } else {
            if (__DEV__) console.warn('⚠️ STEP 4.5: needs-name-collection check returned', nameCheckResponse.status);
          }
        } catch (nameCheckErr) {
          if (__DEV__) console.warn('⚠️ STEP 4.5: Could not check name-collection status:', nameCheckErr);
        }
      }

      if (routingIntent === 'app' && data.needsOnboarding) {
        routingIntent = 'onboarding';
        if (__DEV__) console.log('✅ STEP 4.5: User needs onboarding');
      }

      await storage.setItem('login_routing_intent', routingIntent);
      if (__DEV__) console.log('✅ STEP 4.5 COMPLETE: Routing intent:', routingIntent);

      // Step 5: Update context state
      setUser(data.user || userInfo);
      setIsAuthenticated(true);

      // Step 5.5: Bridge into AuthContext
      const bridgeUser = data.user || userInfo;
      try {
        const { notifyLogin } = require('../auth/authContextBridge');
        await notifyLogin({ id: bridgeUser.id, name: bridgeUser.name, email: bridgeUser.email });
        if (__DEV__) console.log('✅ STEP 5.5: AuthContext bridged');
      } catch (bridgeErr) {
        if (__DEV__) console.warn('⚠️ STEP 5.5: AuthContext bridge failed:', bridgeErr);
      }

      if (__DEV__) console.log('✅ NATIVE APPLE AUTH COMPLETE');
    } catch (err) {
      if (__DEV__) console.error('❌ Auth0: Native Apple Sign-In failed:', err);
      setError(err as Error);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Native Google Sign-In via Auth0 token-exchange grant.
   * Bypasses Universal Login entirely → NO consent screen.
   *
   * Caller (LoginScreen.native.tsx) is responsible for getting the Google
   * ID token from @react-native-google-signin/google-signin.signIn().
   *
   * Requires Auth0 client to have:
   *  - app_type=native
   *  - mobile.ios.app_bundle_identifier=com.coachcedar.app
   *  - mobile.android.app_package_name=com.coachcedar.app
   *  - google-oauth2 connection's "Allowed Mobile Client IDs" includes both
   *    the iOS + Android OAuth client IDs from Google Cloud Console
   *
   * Note: this mirrors loginWithNativeApple but with subject_token_type for Google.
   * Duplicates the post-credential flow intentionally; extract a shared helper
   * after both native flows are validated in production.
   */
  const loginWithNativeGoogle = async ({ idToken }: { idToken: string }) => {
    if (__DEV__) console.log('🔵 LOGIN WITH NATIVE GOOGLE: START');

    if (!auth0Client) {
      throw new Error('Auth0 client not initialized');
    }

    try {
      setIsLoading(true);
      setError(null);

      // Step 1: Exchange Google ID token with Auth0 via token-exchange grant.
      // No webview, no Universal Login, no consent screen.
      // Auth0 verifies the Google ID token (signature + iss + aud) against Google's keys.
      if (__DEV__) console.log('🔵 STEP 1: Token exchange with Auth0...');
      const tokenExchangeBody = {
        grant_type: 'urn:ietf:params:oauth:grant-type:token-exchange',
        subject_token_type: 'http://auth0.com/oauth/token-type/google-id-token',
        subject_token: idToken,
        client_id: auth0Config.clientId,
        audience: auth0Config.audience,
        scope: auth0Config.scope,
      };

      const tokenResponse = await fetch(`https://${auth0Config.domain}/oauth/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(tokenExchangeBody),
      });

      if (!tokenResponse.ok) {
        const errBody = await tokenResponse.json().catch(() => ({} as any));
        const msg = errBody.error_description || errBody.error || `HTTP ${tokenResponse.status}`;
        if (__DEV__) console.log('❌ STEP 1 FAILED: Token exchange returned', tokenResponse.status, errBody);
        throw new Error(`Google token exchange failed: ${msg}`);
      }

      const tokens = await tokenResponse.json();
      const credentials = {
        accessToken: tokens.access_token,
        idToken: tokens.id_token,
        refreshToken: tokens.refresh_token,
      };
      if (__DEV__) console.log('✅ STEP 1 COMPLETE: Got Auth0 credentials via token exchange');

      // Step 2: Get user info from Auth0
      if (__DEV__) console.log('🔵 STEP 2: Getting user info from Auth0...');
      const userInfo = await auth0Client.auth.userInfo({
        token: credentials.accessToken,
      });
      if (__DEV__) console.log('✅ STEP 2 COMPLETE: Got user info');

      // Google returns name/email/picture reliably in userInfo, so no idToken-decode fallback needed
      // (unlike Apple). But keep the same merged shape for downstream consumers.
      const mergedUserInfo = {
        ...userInfo,
        given_name: userInfo.givenName || userInfo.given_name || '',
        family_name: userInfo.familyName || userInfo.family_name || '',
      };
      if (__DEV__) console.log('🔵 STEP 2.5: given_name:', mergedUserInfo.given_name, 'family_name:', mergedUserInfo.family_name);

      // Step 3: Verify with Coach Cedar backend
      const backendUrl = API_BASE_URL;
      if (__DEV__) console.log('🔵 STEP 3: Verifying with backend...');

      const attribution = await readSignupAttribution();

      const response = await fetch(`${backendUrl}/api/auth0/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_token: credentials.accessToken,
          auth0_user: mergedUserInfo,
          ...(attribution ? { attribution } : {}),
        }),
      });

      if (!response.ok) {
        if (__DEV__) console.log('❌ STEP 3 FAILED: Backend verification failed, status:', response.status);
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Backend verification failed');
      }

      const data = await response.json();
      if (__DEV__) console.log('✅ STEP 3 COMPLETE: Backend verification successful');

      // Step 4: Store tokens
      await secureTokenStorage.saveTokens(
        credentials.accessToken,
        credentials.refreshToken,
        data.token
      );
      await secureTokenStorage.saveUserData(data.user);
      // Hydrate feature flags / consent / pipes for THIS session. /auth0/verify's
      // response carries no feature_flags, so a fresh login previously left every
      // gated capability off until the next cold start.
      await hydrateSessionFromToken(data.token);
      if (__DEV__) console.log('✅ STEP 4 COMPLETE: Tokens and user data stored');

      // Step 4.5: Routing intent
      if (userInfo.email) {
        await storage.setItem('user_email', userInfo.email);
      }

      let routingIntent = 'app';
      // HIGHEST PRIORITY: email verification wall (mirrors loginWithRedirect logic).
      // Google users return email_verified=true from the provider so this is a
      // defense-in-depth check rather than an expected hit on the native Google path.
      if (data?.needsEmailVerification) {
        routingIntent = 'email-verification';
        if (__DEV__) console.log('🚧 STEP 4.5: User needs email verification — routing to wall');
      }
      const userAuthMethod = data.user?.authMethod || 'google'; // native Google flow
      if (__DEV__) console.log('🔵 STEP 4.5: auth_method for name check:', userAuthMethod);
      if (routingIntent === 'app') {
        try {
          const nameCheckResponse = await fetch(`${backendUrl}/api/users/needs-name-collection?auth_method=${encodeURIComponent(userAuthMethod)}`, {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${data.token}` },
          });
          if (nameCheckResponse.ok) {
            const nameCheckData = await nameCheckResponse.json();
            if (__DEV__) console.log('🔵 STEP 4.5: name check response:', JSON.stringify(nameCheckData));
            if (nameCheckData.needs_name_collection) {
              routingIntent = 'name-collection';
              if (__DEV__) console.log('✅ STEP 4.5: User needs name collection');
            }
          } else {
            if (__DEV__) console.warn('⚠️ STEP 4.5: needs-name-collection check returned', nameCheckResponse.status);
          }
        } catch (nameCheckErr) {
          if (__DEV__) console.warn('⚠️ STEP 4.5: Could not check name-collection status:', nameCheckErr);
        }
      }

      if (routingIntent === 'app' && data.needsOnboarding) {
        routingIntent = 'onboarding';
        if (__DEV__) console.log('✅ STEP 4.5: User needs onboarding');
      }

      await storage.setItem('login_routing_intent', routingIntent);
      if (__DEV__) console.log('✅ STEP 4.5 COMPLETE: Routing intent:', routingIntent);

      // Step 5: Update context state
      setUser(data.user || userInfo);
      setIsAuthenticated(true);

      // Step 5.5: Bridge into AuthContext
      const bridgeUser = data.user || userInfo;
      try {
        const { notifyLogin } = require('../auth/authContextBridge');
        await notifyLogin({ id: bridgeUser.id, name: bridgeUser.name, email: bridgeUser.email });
        if (__DEV__) console.log('✅ STEP 5.5: AuthContext bridged');
      } catch (bridgeErr) {
        if (__DEV__) console.warn('⚠️ STEP 5.5: AuthContext bridge failed:', bridgeErr);
      }

      if (__DEV__) console.log('✅ NATIVE GOOGLE AUTH COMPLETE');
    } catch (err) {
      if (__DEV__) console.error('❌ Auth0: Native Google Sign-In failed:', err);
      setError(err as Error);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithPopup = async (options?: any) => {
    // Native doesn't use popup, redirect to loginWithRedirect
    return loginWithRedirect(options);
  };

  const logout = async (options?: any) => {
    try {
      setIsLoading(true);

      // Fire the sign-out kill-signal (plan O6) while the JWT is still valid —
      // clearAll() below wipes it and the authed /events POST would 401.
      try {
        const { trackWidget } = await import('../services/analyticsEvents');
        trackWidget('data_cleared', { source: 'logout' }).catch(() => {});
      } catch { /* best-effort */ }

      // Clear all secure storage tokens and auth state
      await secureTokenStorage.clearAll();
      // Plan §A.11: Clear the per-user health-permission trigger guard so a
      // different user signing in on this device is not mistakenly blocked
      // from being offered phone-health permissions.
      try {
        const uidForKey = await storage.getItem('user_id');
        if (uidForKey) {
          await storage.removeItem(`${uidForKey}_health_permission_triggered`);
        }
      } catch { /* best-effort */ }
      // Clear iOS home-widget App Group data (Pocket Coach) — sets the write
      // latch, clears the native blob, removes the per-user content cache. Covers
      // the provider-level / token-expiry logout in addition to settings logout,
      // so the prior user's plan can't survive onto the next account's lock screen.
      try {
        const uidForWidget = await storage.getItem('user_id');
        // Remove user_id BEFORE clearing the widget so any pushHomeWidget racing
        // this logout observes no session (hasValidSession=false) and can't
        // re-populate the blob after the clear. clearHomeWidget still gets the
        // captured id for the per-user cache removal. (Mirrors useSettingsLogic,
        // which removes user_id before its clearHomeWidget call.)
        await storage.removeItem('user_id');
        const { clearHomeWidget } = await import('../services/homeWidgetWriter');
        await clearHomeWidget(uidForWidget ?? undefined);
        const { clearUserTimezone } = await import('../services/userTimezoneSync');
        clearUserTimezone();
      } catch { /* best-effort */ }
      // Clear any login routing intent and cached email stored during sign-in
      await storage.removeItem('login_routing_intent');
      await storage.removeItem('user_email');
      // 🚨 CRITICAL FIX (Mar 11, 2026): Clear session data that leaks between users
      // ROOT CAUSE: contextual_session, conversation_id, and chatInitialized from a
      // previous user persist in AsyncStorage and contaminate the new user's requests.
      // This caused the "Yes" no-response bug: stale contextual_launch from old user
      // triggered CONTEXTUAL BYPASS, skipping the onboarding engine entirely.
      await storage.removeItem('contextual_session');
      await storage.removeItem('conversation_id');
      await storage.removeItem('chatInitialized');
      // 🔧 FIX: Do NOT clear onboarding_completed — it's a permanent fact about the user,
      // backed by the database. Clearing it causes returning users to be re-routed through
      // onboarding on every logout/login cycle. The backend /api/users/onboarding-state
      // endpoint is the source of truth; local storage is just a cache.
      // await storage.removeItem('onboarding_completed');
      // 🔧 M35 FIX: Clear remaining per-session keys that leak between users.
      // user_id + cedar_user identify the logged-in user; purchase_completed gates
      // paywall; deferredWorkouts + planModifications cache user-specific plan edits.
      await storage.removeItem('user_id');
      await storage.removeItem('cedar_user');
      await storage.removeItem('user_name');
      await storage.removeItem('purchase_completed');
      // 🔧 M35B FIX 1.5: Clear SIWA name keys on logout to prevent cross-user
      // contamination if a different Apple ID logs in on the same device.
      await storage.removeItem('siwa_first_name');
      await storage.removeItem('siwa_last_name');
      await storage.removeItem('siwa_auth_timestamp');
      await storage.removeItem('deferredWorkouts');
      await storage.removeItem('planModifications');
      await storage.removeItem('notification_permission_asked');
      // Clear the push-permission ledger so a different user on the same device
      // starts from a clean ask state (single source of truth for the key).
      await clearPushPermissionLedger();
      // Clear the cached first-plan timestamp too (notifications-first Phase 2)
      // so a different user on the same device can't inherit it.
      await clearFirstPlanCreatedAt();
      await storage.removeItem('pending_health_sync_message');
      // 🔧 M35B FIX: Clear persisted onboarding tour trigger to prevent stale
      // replays if a different user logs in on the same device.
      await storage.removeItem('__pending_tour_trigger');

      // 🔧 FIX (Mar 18, 2026): Unregister push token on logout to prevent
      // User B from receiving User A's notifications on shared devices
      // 🔧 FIX (Mar 25, 2026): Send both `platform` AND `push_token` fields —
      // backend needs `platform` or `push_token` (was only sending push_token
      // which the old backend rejected as missing platform → silent 400 error)
      try {
        const { Platform } = require('react-native');
        if (Platform.OS !== 'web') {
          const token = await storage.getItem('expo_push_token');
          const jwt = await secureTokenStorage.getCedarJwt();
          if (token && jwt) {
            await fetch(`${process.env.EXPO_PUBLIC_API_URL || 'https://api.coachcedar.com'}/api/push/unregister-native`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${jwt}` },
              body: JSON.stringify({ platform: Platform.OS, push_token: token }),
            }).catch(() => {}); // Best-effort — don't block logout
          }
          await storage.removeItem('expo_push_token');
        }
      } catch {}

      setUser(null);
      setIsAuthenticated(false);

      // ✅ On native platforms we deliberately skip clearSession().
      // clearSession() opens ASWebAuthenticationSession which shows the system
      // "Wants to Use ... to Sign In" dialog — misleading for a logout action —
      // and then redirects to the login callback URI which produces an error page.
      // Clearing local tokens is sufficient: the next login uses prompt=select_account
      // / max_age=0 so Auth0 will force fresh authentication regardless.
      if (__DEV__) console.log('✅ Auth: Logout complete (local tokens cleared)');
    } catch (err: any) {
      if (__DEV__) console.warn('⚠️ Auth: Logout cleanup error (user still logged out locally):', err?.message || err);
    } finally {
      setIsLoading(false);
      navigateAfterLogout(router);
    }
  };

  // Memoised so EnhancedAuthGuard's useEffect dependency on this function
  // doesn't re-fire the effect on every render (which caused an infinite
  // getCedarJwt loop at boot).
  const getAccessTokenSilently = useCallback(async (_options?: any) => {
    if (!auth0Client) {
      throw new Error('Auth0 client not initialized');
    }

    // 🚨 CRITICAL FIX: Return stored token instead of throwing error
    // This fixes "Silent token refresh not yet implemented for native" error
    try {
      const token = await secureTokenStorage.getCedarJwt();
      
      if (!token) {
        throw new Error('No stored token found');
      }
      
      // Optionally verify token is still valid
      // For now, just return it (backend will reject if invalid)
      if (__DEV__) console.log('✅ Auth: Returning stored token');
      return token;
    } catch (error) {
      if (__DEV__) console.error('❌ Auth: Failed to get access token:', error);
      throw error;
    }
  }, [auth0Client]);

  const value: Auth0ContextValue = {
    isAuthenticated,
    isLoading,
    user,
    error,
    loginWithRedirect,
    loginWithPopup,
    logout,
    getAccessTokenSilently,
    loginWithNativeApple,
    loginWithNativeGoogle,
  };

  return <Auth0Context.Provider value={value}>{children}</Auth0Context.Provider>;
};

/**
 * Web implementation: the Auth0 React hook, plus stubs for the native-only
 * methods. Only ever selected when `useAuth0Web` resolved (see below).
 */
const useUnifiedAuth0Web = (): Auth0ContextValue => {
  const webCtx = useAuth0Web();
  return {
    ...webCtx,
    // Native-only method — no-op on web (web doesn't have native Apple SDK)
    loginWithNativeApple: async () => {
      if (__DEV__) console.warn('loginWithNativeApple is native-only; ignoring call on web');
    },
    // Native-only method — no-op on web (web doesn't have native Google SDK)
    loginWithNativeGoogle: async () => {
      if (__DEV__) console.warn('loginWithNativeGoogle is native-only; ignoring call on web');
    },
  };
};

/** Native implementation: the custom context this provider renders. */
const useUnifiedAuth0Native = (): Auth0ContextValue => {
  const context = useContext(Auth0Context);
  if (!context) {
    throw new Error('useUnifiedAuth0 must be used within UnifiedAuth0Provider');
  }
  return context;
};

/**
 * Unified Auth0 Hook - works on both web and native
 *
 * 2026-09-17 (hooks debt): this used to be ONE function that branched on
 * `Platform.OS === 'web' && useAuth0Web` and called a different hook in each
 * arm — lint-true, runtime-false, because both operands are fixed for the life
 * of the JS process: `Platform.OS` is a constant and `useAuth0Web` is assigned
 * exactly once, synchronously, at the top of this module and never again. So
 * the SAME condition is now evaluated once, here, and picks the implementation;
 * each implementation calls its hooks unconditionally. Behaviour is identical
 * on both platforms, and the file is now inside the BLOCKING hooks lint.
 */
export const useUnifiedAuth0: () => Auth0ContextValue =
  Platform.OS === 'web' && useAuth0Web ? useUnifiedAuth0Web : useUnifiedAuth0Native;

// Export for backward compatibility
export { useUnifiedAuth0 as useAuth0 };

